// /////////////////////////////////////////////////////////////////////////////////////
//                              Copyright (c) 2020 - 2026
//                                    RXO Inc.
//                          All Rights Reserved Worldwide
//
// WARNING:  This program (or document) is unpublished, proprietary
// property of RXO Inc. and is to be maintained in strict confidence.
// Unauthorized reproduction, distribution or disclosure of this program
// (or document), or any program (or document) derived from it is
// prohibited by State and Federal law, and by local law outside of the U.S.
// /////////////////////////////////////////////////////////////////////////////////////

import React, { useState, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation } from "react-router-dom";
import { isBefore } from "date-fns";

import AwardUploadModal from "../award-upload/AwardUploadModal";
import OpenNextRoundModal from "../open-next-round/OpenNextRoundModal";
import {
  resetBannerActionState,
  closeBid,
  closeBidWon,
  resetCloseBidActionState,
  resetCloseBidWonActionState,
  fetchBidSummary,
  fetchBidMessages
} from "../../../slices/bidSummarySlice";
import { userRole } from "../../../helpers/types";
import CloseBidModal from "../award-import/CloseBidModal";
import messages from "../../../helpers/messages";
import { isEffectiveCoyoteBid } from "../../../helpers/isEffectiveCoyoteBid";

function SubmittedBidState({ summary, user }) {
  const dispatch = useDispatch();
  const {
    closeBidActionIsLoading,
    closeBidActionFailure,
    closeBidWonActionIsLoading,
    closeBidWonActionFailure
  } = useSelector(state => state.bidSummary);
  const { enableCloseBidWon, selfPricedCloseWon } =
    useSelector(state => state.featureFlags);
  const route = useLocation().pathname.split("/")[3] || "";

  const [openAwardUploadModal, setOpenAwardUploadModal] = useState(false);
  const [openNextRoundModal, setOpenNextRoundModal] = useState(false);
  const [openCloseBidModal, setOpenCloseBidModal] = useState(false);

  const { closeDateUTC, modes, bidId } = summary;
  const { userId, role } = user;
  const isManagementUser = role === userRole.management;
  const isSelfPriced = modes.some(m => m.bidOwnerRepType === 1 && m.mode === 1);
  const effectiveCoyoteBid = isEffectiveCoyoteBid(summary);
  const shouldShowCloseBidButton = enableCloseBidWon 
    ? (effectiveCoyoteBid || isManagementUser || isSelfPriced)
    : true;
  const shouldHideButtonsForRxo = !effectiveCoyoteBid && !isSelfPriced;

  const getText = () => {
    if (
      isBefore(
        new Date(closeDateUTC).setHours(0, 0, 0, 0),
        new Date().setHours(0, 0, 0, 0)
      )
    ) {
      return messages.bidDeadlinePassedMessage.defaultMessage;
    } else if (isBidOwner) {
      return messages.pricingIsCompleteForBidOwnerMessage.defaultMessage;
    } else {
      return messages.pricingIsCompleteForNonBidOwnerMessage.defaultMessage;
    }
  };

  const isBidOwner = modes.some(x => x.bidOwnerId === userId);
  const text = getText();

  const handleOpenNextRoundClick = useCallback(() => {
    dispatch(resetBannerActionState());
    setOpenNextRoundModal(true);
  }, [dispatch]);

  const handleUploadAwardClick = useCallback(() => {
    dispatch(resetBannerActionState());
    setOpenAwardUploadModal(true);
  }, [dispatch]);

  const handleCloseBidClick = crmPayload => {
    dispatch(closeBid(bidId, role, route, crmPayload));
  };

  const handleCloseWonClick = () => {
    if (enableCloseBidWon) {
      dispatch(closeBidWon(bidId, role, route));
    }
  };

  const handleClose = fetchDetails => {
    if (fetchDetails) {
      dispatch(resetCloseBidActionState());
      if (enableCloseBidWon) {
        dispatch(resetCloseBidWonActionState());
      }
      dispatch(fetchBidSummary({ bidId, role }));
      route === "summary" && dispatch(fetchBidMessages(bidId));
    }
    setOpenCloseBidModal(false);
  };

  return (
    <>
      <p className="inline-block font-size-14 pt-16 mb-0">{text}</p>
      <div className="fr py-8 my-2">
        {!shouldHideButtonsForRxo && (
          <button
            className="text-button univers-67-bold-cond font-size-14 uppercase mx-8 h-32 px-12"
            data-testid="open-round"
            onClick={handleOpenNextRoundClick}
          >
            {messages.openNextRound.defaultMessage}
          </button>
        )}
        {!shouldHideButtonsForRxo && (
          <button
            className="text-button univers-67-bold-cond font-size-14 uppercase mx-8 h-32 short-button px-12"
            data-testid="upload-award"
            onClick={handleUploadAwardClick}
          >
            {messages.uploadAward.defaultMessage}
          </button>
        )}
        {shouldShowCloseBidButton && (
          <button
            className="text-button univers-67-bold-cond font-size-14 uppercase mx-8 short-button h-32"
            data-testid="close-bid"
            onClick={() => setOpenCloseBidModal(true)}
          >
            {messages.closeBid.defaultMessage}
          </button>
        )}
      </div>
      {openAwardUploadModal && (
        <AwardUploadModal
          open={true}
          summary={summary}
          user={user}
          selfPricedCloseWon={Boolean(selfPricedCloseWon)}
          handleModalClose={() => setOpenAwardUploadModal(false)}
        />
      )}
      {openNextRoundModal && (
        <OpenNextRoundModal
          open={true}
          summary={summary}
          role={role}
          handleModalClose={() => setOpenNextRoundModal(false)}
        />
      )}
      {openCloseBidModal && (
        <CloseBidModal
          handleClose={handleClose}
          handleCloseBidClick={handleCloseBidClick}
          handleCloseWonClick={
            enableCloseBidWon && (!isSelfPriced || selfPricedCloseWon)
              ? handleCloseWonClick
              : undefined
          }
          isLoading={closeBidActionIsLoading || (enableCloseBidWon && closeBidWonActionIsLoading)}
          isFailure={closeBidActionFailure || (enableCloseBidWon && closeBidWonActionFailure)}
          summary={summary}
        />
      )}
    </>
  );
}

export default SubmittedBidState;
